import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";

const SERBIA_CENTER = [44.0165, 21.0059];

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1400&auto=format&fit=crop";

const MAP_FILTERS = [
  { id: "places", label: "Mesta" },
  { id: "hosts", label: "Hostovi" },
  { id: "adventures", label: "Avanture" },
  { id: "accommodation", label: "Smeštaj" },
  { id: "services", label: "Usluge" },
  { id: "rental", label: "Iznajmljivanje" },
];

const SERVICE_CATEGORIES = [
  "Vodič",
  "Instruktor",
  "Prevoz / transfer",
  "Fotografija / video",
  "Organizacija / team building",
  "Servis / podrška",
  "Ostalo",
];

const RENTAL_CATEGORIES = [
  "Bicikli / e-bike",
  "Kajak / SUP",
  "Čamac",
  "Quad / ATV",
  "Ski / snowboard oprema",
  "Kamp oprema",
  "Planinarska oprema",
  "Penjačka / via ferrata oprema",
  "Ostalo",
];

const LEGACY_ADVENTURE_OFFER_CATEGORIES = new Set([
  "Planinarenje",
  "Rafting",
  "Kajak / SUP",
  "Biciklizam / MTB",
  "Off-road",
  "Kampovanje",
  "Penjanje / Via ferrata",
  "Speleologija",
]);

function inferOfferType(offer) {
  if (offer?.offer_type) return offer.offer_type;

  const category = String(offer?.category || "").trim();
  if (category === "Iznajmljivanje opreme") return "rental";
  if (LEGACY_ADVENTURE_OFFER_CATEGORIES.has(category)) return "adventure";
  return "service";
}

function Icon({ name, size = 20, strokeWidth = 2 }) {
  const icons = {
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </>
    ),
    mapPin: (
      <>
        <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    compass: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
      </>
    ),
    navigation: <path d="m3 11 18-8-8 18-2-8-8-2Z" />,
    layers: (
      <>
        <path d="m12 3 8 4-8 4-8-4 8-4Z" />
        <path d="m4 12 8 4 8-4M4 17l8 4 8-4" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20v-2a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v2" />
        <path d="M16 4.5a3 3 0 0 1 0 6M17 13a5 5 0 0 1 4 5v2" />
      </>
    ),
    camera: (
      <>
        <path d="M14.5 4 16 6h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l1.5-2Z" />
        <circle cx="12" cy="12" r="3.5" />
      </>
    ),
    heart: (
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    ),
    trophy: (
      <>
        <path d="M8 4h8v5a4 4 0 0 1-8 0Z" />
        <path d="M8 6H4v2a4 4 0 0 0 4 4M16 6h4v2a4 4 0 0 1-4 4" />
        <path d="M12 13v4M8 21h8M9 17h6" />
      </>
    ),
    close: (
      <>
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
      </>
    ),
    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),
    filter: (
      <>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </>
    ),
    sparkle: (
      <>
        <path d="m12 3 1.1 3.3L16 8l-2.9 1.7L12 13l-1.1-3.3L8 8l2.9-1.7L12 3Z" />
        <path d="m18 14 .7 2.3L21 17l-2.3.7L18 20l-.7-2.3L15 17l2.3-.7L18 14Z" />
      </>
    ),
    chevronUp: <path d="m6 15 6-6 6 6" />,
    chevronDown: <path d="m6 9 6 6 6-6" />,
    locate: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      </>
    ),
    route: (
      <>
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="6" r="2" />
        <path d="M8 18h2a4 4 0 0 0 4-4v-4a4 4 0 0 1 4-4" />
      </>
    ),
    grid: (
      <>
        <rect x="4" y="4" width="6" height="6" rx="1.5" />
        <rect x="14" y="4" width="6" height="6" rx="1.5" />
        <rect x="4" y="14" width="6" height="6" rx="1.5" />
        <rect x="14" y="14" width="6" height="6" rx="1.5" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getPlaceImage(place) {
  return (
    place?.cover_url ||
    place?.primary_photo_url ||
    FALLBACK_COVER
  );
}

function makePlaceMarker(place, active) {
  const image = escapeHtml(getPlaceImage(place));
  const name = escapeHtml(place.name || "Outdoor mesto");
  const category = escapeHtml(
    place.place_categories?.name || "Outdoor"
  );

  if (!active) {
    return L.divIcon({
      className: "moSimplePlaceMarkerShell",
      html: `
        <div class="moSimplePlaceMarker">
          <span></span>
          <i></i>
        </div>
      `,
      iconSize: [34, 42],
      iconAnchor: [17, 38],
    });
  }

  return L.divIcon({
    className: "moActivePlaceMarkerShell",
    html: `
      <div class="moActivePlaceMarker">
        <div class="moActivePlacePhoto">
          <img src="${image}" alt="" />
          <div class="moActivePlaceShade"></div>
          <span>${category}</span>
          <strong>${name}</strong>
        </div>
        <i></i>
      </div>
    `,
    iconSize: [132, 126],
    iconAnchor: [66, 120],
  });
}

function makeHostMarker(host, active) {
  const name = escapeHtml(
    host?.full_name || host?.username || "Outdoor host"
  );
  const initial = escapeHtml(
    (host?.full_name || host?.username || "H")
      .trim()
      .charAt(0)
      .toUpperCase() || "H"
  );

  const avatar = host?.map_image_url
    ? escapeHtml(host.map_image_url)
    : "";

  return L.divIcon({
    className: active
      ? "moSimpleHostMarkerShell active"
      : "moSimpleHostMarkerShell",
    html: `
      <div class="moSimpleHostMarker">
        <span class="moSimpleHostAvatar">
          ${avatar ? `<img src="${avatar}" alt="" />` : `<b>${initial}</b>`}
        </span>
        ${active ? `<strong>${name}</strong>` : ""}
        <i></i>
      </div>
    `,
    iconSize: active ? [150, 58] : [48, 54],
    iconAnchor: active ? [30, 52] : [24, 48],
  });
}

const USER_MARKER = L.divIcon({
  className: "moUserMarkerShell",
  html: `
    <div class="moUserMarker">
      <span></span>
      <i></i>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

function AutoLocateController({
  requestId,
  onStart,
  onSuccess,
  onError,
}) {
  const map = useMap();

  useEffect(() => {
    if (!requestId) return;

    onStart();

    if (!navigator.geolocation) {
      onError("GPS nije dostupan na ovom uređaju.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const result = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || 0,
        };

        map.flyTo(
          [result.latitude, result.longitude],
          14,
          {
            duration: 1.25,
            easeLinearity: 0.25,
          }
        );

        onSuccess(result);
      },
      (error) => {
        const messages = {
          1: "Lokacija nije dozvoljena.",
          2: "Lokacija trenutno nije dostupna.",
          3: "GPS je predugo čekao odgovor.",
        };

        onError(
          messages[error?.code] ||
            "Nismo uspeli da pronađemo tvoju lokaciju."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      }
    );
  }, [map, onError, onStart, onSuccess, requestId]);

  return null;
}

function FocusPlace({ place }) {
  const map = useMap();

  useEffect(() => {
    if (!place) return;

    map.flyTo(
      [Number(place.latitude), Number(place.longitude)],
      14,
      {
        duration: 0.75,
        easeLinearity: 0.22,
      }
    );
  }, [map, place]);

  return null;
}

function formatNumber(value) {
  return new Intl.NumberFormat("sr-Latn-RS").format(
    Number(value || 0)
  );
}

function distanceKm(from, place) {
  if (!from || !place) return null;

  const lat1 = (from.latitude * Math.PI) / 180;
  const lat2 = (Number(place.latitude) * Math.PI) / 180;
  const dLat =
    ((Number(place.latitude) - from.latitude) * Math.PI) / 180;
  const dLon =
    ((Number(place.longitude) - from.longitude) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(dLon / 2) ** 2;

  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(value) {
  if (value == null) return "";

  if (value < 1) {
    return `${Math.max(1, Math.round(value * 1000))} m`;
  }

  if (value < 10) {
    return `${value.toFixed(1)} km`;
  }

  return `${Math.round(value)} km`;
}

export default function ExploreMap() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [places, setPlaces] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedHostId, setSelectedHostId] = useState(null);
  const [activeFilters, setActiveFilters] = useState([
    "places",
    "hosts",
  ]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [rentalCategories, setRentalCategories] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(true);
  const [locateRequest, setLocateRequest] = useState(1);
  const [locateStatus, setLocateStatus] = useState("idle");
  const [locateMessage, setLocateMessage] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlaces = useCallback(async () => {
    const { data, error } = await supabase
      .from("places")
      .select(`
        id,
        name,
        short_description,
        country_name,
        region,
        municipality,
        locality,
        latitude,
        longitude,
        cover_url,
        visitors_count,
        checkins_count,
        photos_count,
        saves_count,
        location_precision,
        created_at,
        place_categories:category_id (
          id,
          code,
          name,
          icon
        )
      `)
      .eq("is_active", true)
      .order("visitors_count", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(1000);

    if (error) {
      console.error("Explore places:", error);
      return;
    }

    const basePlaces = data || [];

    if (basePlaces.length === 0) {
      setPlaces([]);
      return;
    }

    const placeIds = basePlaces.map((place) => place.id);

    const { data: photoRows, error: photoError } = await supabase
      .from("place_photos")
      .select("place_id, image_url, created_at")
      .in("place_id", placeIds)
      .eq("moderation_status", "approved")
      .order("created_at", { ascending: true });

    if (photoError) {
      console.warn(
        "Explore marker photo fallback:",
        photoError
      );

      setPlaces(
        basePlaces.map((place) => ({
          ...place,
          primary_photo_url: null,
        }))
      );

      return;
    }

    const firstPhotoByPlace = new Map();

    (photoRows || []).forEach((photo) => {
      if (
        photo.place_id &&
        photo.image_url &&
        !firstPhotoByPlace.has(photo.place_id)
      ) {
        firstPhotoByPlace.set(
          photo.place_id,
          photo.image_url
        );
      }
    });

    setPlaces(
      basePlaces.map((place) => ({
        ...place,
        primary_photo_url:
          firstPhotoByPlace.get(place.id) || null,
      }))
    );
  }, []);

  const loadHosts = useCallback(async () => {
    const { data: hostRows, error: hostError } = await supabase
      .from("profiles")
      .select(`
        id,
        username,
        full_name,
        avatar_url,
        city,
        country,
        public_location,
        latitude,
        longitude
      `)
      .eq("role", "host")
      .not("latitude", "is", null)
      .not("longitude", "is", null);

    if (hostError) {
      console.error("Explore hosts:", hostError);
      setHosts([]);
      return;
    }

    const hostIds = (hostRows || []).map((host) => host.id);

    if (hostIds.length === 0) {
      setHosts([]);
      return;
    }

    const [eventsResult, accommodationsResult, offersResult] =
      await Promise.all([
        supabase
          .from("events")
          .select("host_id, cover_url")
          .in("host_id", hostIds),
        supabase
          .from("host_accommodations")
          .select("host_id, cover_url")
          .in("host_id", hostIds)
          .eq("is_active", true),
        supabase
          .from("host_offers")
          .select("host_id, offer_type, category, cover_url")
          .in("host_id", hostIds)
          .eq("is_active", true),
      ]);

    if (eventsResult.error) {
      console.warn("Explore host adventures:", eventsResult.error);
    }
    if (accommodationsResult.error) {
      console.warn(
        "Explore host accommodation:",
        accommodationsResult.error
      );
    }
    if (offersResult.error) {
      console.warn("Explore host offers:", offersResult.error);
    }

    const adventureHosts = new Set(
      (eventsResult.data || [])
        .map((row) => row.host_id)
        .filter(Boolean)
    );
    const accommodationHosts = new Set(
      (accommodationsResult.data || [])
        .map((row) => row.host_id)
        .filter(Boolean)
    );
    const serviceHosts = new Set();
    const rentalHosts = new Set();
    const serviceCategoriesByHost = new Map();
    const rentalCategoriesByHost = new Map();
    const mapImageByHost = new Map();

    (eventsResult.data || []).forEach((event) => {
      if (event?.host_id && event?.cover_url && !mapImageByHost.has(event.host_id)) {
        mapImageByHost.set(event.host_id, event.cover_url);
      }
    });

    (accommodationsResult.data || []).forEach((stay) => {
      if (stay?.host_id && stay?.cover_url && !mapImageByHost.has(stay.host_id)) {
        mapImageByHost.set(stay.host_id, stay.cover_url);
      }
    });

    (offersResult.data || []).forEach((offer) => {
      if (!offer?.host_id) return;

      const type = inferOfferType(offer);
      const category = String(offer.category || "").trim();

      if (offer.cover_url && !mapImageByHost.has(offer.host_id)) {
        mapImageByHost.set(offer.host_id, offer.cover_url);
      }

      if (type === "adventure") {
        adventureHosts.add(offer.host_id);
      }

      if (type === "service") {
        serviceHosts.add(offer.host_id);
        if (!serviceCategoriesByHost.has(offer.host_id)) {
          serviceCategoriesByHost.set(offer.host_id, new Set());
        }
        if (category) {
          serviceCategoriesByHost.get(offer.host_id).add(category);
        }
      }

      if (type === "rental") {
        rentalHosts.add(offer.host_id);
        if (!rentalCategoriesByHost.has(offer.host_id)) {
          rentalCategoriesByHost.set(offer.host_id, new Set());
        }
        if (category) {
          rentalCategoriesByHost.get(offer.host_id).add(category);
        }
      }
    });

    setHosts(
      (hostRows || []).map((host) => ({
        ...host,
        capabilities: {
          adventures: adventureHosts.has(host.id),
          accommodation: accommodationHosts.has(host.id),
          services: serviceHosts.has(host.id),
          rental: rentalHosts.has(host.id),
        },
        serviceCategories: Array.from(
          serviceCategoriesByHost.get(host.id) || []
        ),
        rentalCategories: Array.from(
          rentalCategoriesByHost.get(host.id) || []
        ),
        map_image_url:
          host.avatar_url ||
          mapImageByHost.get(host.id) ||
          "",
      }))
    );
  }, []);

  const loadCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from("place_categories")
      .select("id, name, code, sort_order")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });

    if (!error) setCategories(data || []);
  }, []);

  useEffect(() => {
    let active = true;

    async function boot() {
      setLoading(true);

      await Promise.all([
        loadPlaces(),
        loadHosts(),
        loadCategories(),
      ]);

      if (active) {
        setLoading(false);
      }
    }

    boot();

    return () => {
      active = false;
    };
  }, [loadCategories, loadHosts, loadPlaces]);

  useEffect(() => {
    const channel = supabase
      .channel("explore-live")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "places",
        },
        loadPlaces
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "place_checkins",
        },
        loadPlaces
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "place_photos",
        },
        loadPlaces
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
        },
        loadHosts
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "events",
        },
        loadHosts
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "host_accommodations",
        },
        loadHosts
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "host_offers",
        },
        loadHosts
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadHosts, loadPlaces]);

  const handleLocateStart = useCallback(() => {
    setLocateStatus("locating");
    setLocateMessage("Pronalazimo te...");
  }, []);

  const handleLocateSuccess = useCallback((location) => {
    setUserLocation(location);
    setLocateStatus("found");
    setLocateMessage("Tu si.");

    window.setTimeout(() => {
      setLocateStatus("idle");
    }, 1500);
  }, []);

  const handleLocateError = useCallback((message) => {
    setLocateStatus("error");
    setLocateMessage(message);

    window.setTimeout(() => {
      setLocateStatus("idle");
    }, 3000);
  }, []);

  function openNavigation(place) {
    if (!place?.latitude || !place?.longitude) return;

    const latitude = Number(place.latitude);
    const longitude = Number(place.longitude);

    const url =
      `https://www.google.com/maps/dir/?api=1` +
      `&destination=${encodeURIComponent(
        `${latitude},${longitude}`
      )}` +
      `&travelmode=driving`;

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  }

  const filteredPlaces = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return places
      .filter((place) => {
        if (
          categoryId !== "all" &&
          place.place_categories?.id !== categoryId
        ) {
          return false;
        }

        if (!needle) return true;

        return [
          place.name,
          place.region,
          place.locality,
          place.municipality,
          place.place_categories?.name,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(needle)
          );
      })
      .map((place) => ({
        ...place,
        distanceKm: distanceKm(userLocation, place),
      }))
      .sort((a, b) => {
        if (userLocation) {
          return (
            (a.distanceKm ?? Number.POSITIVE_INFINITY) -
            (b.distanceKm ?? Number.POSITIVE_INFINITY)
          );
        }

        return (
          Number(b.visitors_count || 0) -
          Number(a.visitors_count || 0)
        );
      });
  }, [categoryId, places, query, userLocation]);

  const offerTypeFilters = useMemo(
    () =>
      activeFilters.filter((filter) =>
        [
          "adventures",
          "accommodation",
          "services",
          "rental",
        ].includes(filter)
      ),
    [activeFilters]
  );

  const filteredHosts = useMemo(() => {
    const hostsVisible =
      activeFilters.includes("hosts") ||
      offerTypeFilters.length > 0;

    if (!hostsVisible) return [];

    const needle = query.trim().toLowerCase();

    return hosts
      .filter((host) => {
        if (offerTypeFilters.length > 0) {
          const matchesCapability = offerTypeFilters.some(
            (filter) => Boolean(host.capabilities?.[filter])
          );

          if (!matchesCapability) return false;
        }

        if (
          activeFilters.includes("services") &&
          serviceCategories.length > 0 &&
          !serviceCategories.some((category) =>
            host.serviceCategories?.includes(category)
          )
        ) {
          return false;
        }

        if (
          activeFilters.includes("rental") &&
          rentalCategories.length > 0 &&
          !rentalCategories.some((category) =>
            host.rentalCategories?.includes(category)
          )
        ) {
          return false;
        }

        if (!needle) return true;

        return [
          host.full_name,
          host.username,
          host.public_location,
          host.city,
          host.country,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(needle)
          );
      })
      .map((host) => ({
        ...host,
        distanceKm: distanceKm(userLocation, host),
      }))
      .sort((a, b) => {
        if (!userLocation) return 0;

        return (
          (a.distanceKm ?? Number.POSITIVE_INFINITY) -
          (b.distanceKm ?? Number.POSITIVE_INFINITY)
        );
      });
  }, [
    activeFilters,
    hosts,
    offerTypeFilters,
    query,
    rentalCategories,
    serviceCategories,
    userLocation,
  ]);

  const selectedHost = useMemo(
    () =>
      hosts.find((host) => host.id === selectedHostId) || null,
    [hosts, selectedHostId]
  );

  const selectedHostDistance = useMemo(
    () =>
      selectedHost
        ? distanceKm(userLocation, selectedHost)
        : null,
    [selectedHost, userLocation]
  );

  function toggleMapFilter(filterId) {
    setActiveFilters((current) =>
      current.includes(filterId)
        ? current.filter((item) => item !== filterId)
        : [...current, filterId]
    );
  }

  function toggleCategoryFilter(category, type) {
    const setter =
      type === "service"
        ? setServiceCategories
        : setRentalCategories;

    setter((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category]
    );
  }

  const selectedPlace = useMemo(
    () =>
      places.find(
        (place) => place.id === selectedPlaceId
      ) || null,
    [places, selectedPlaceId]
  );

  const selectedDistance = useMemo(
    () =>
      selectedPlace
        ? distanceKm(userLocation, selectedPlace)
        : null,
    [selectedPlace, userLocation]
  );

  const totals = useMemo(
    () =>
      places.reduce(
        (accumulator, place) => {
          accumulator.checkins += Number(
            place.checkins_count || 0
          );

          accumulator.photos += Number(
            place.photos_count || 0
          );

          return accumulator;
        },
        {
          checkins: 0,
          photos: 0,
        }
      ),
    [places]
  );

  const nearestPlaces = useMemo(
    () => filteredPlaces.slice(0, 4),
    [filteredPlaces]
  );

  if (loading) {
    return (
      <>
        <ExploreStyles />

        <main className="moLoading">
          <div className="moLoadingOrb">
            <Icon name="compass" size={24} />
          </div>

          <strong>Budimo outdoor mapu...</strong>
          <span>
            Pronalazimo mesta, fotografije i tragove.
          </span>
        </main>
      </>
    );
  }

  return (
    <>
      <ExploreStyles />

      <main className="moExplore">
        <div className="moMapStage">
          <MapContainer
            center={SERBIA_CENTER}
            zoom={7}
            minZoom={5}
            scrollWheelZoom
            className="moLeaflet"
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <AutoLocateController
              requestId={locateRequest}
              onStart={handleLocateStart}
              onSuccess={handleLocateSuccess}
              onError={handleLocateError}
            />

            <FocusPlace place={selectedHost || selectedPlace} />

            {userLocation && (
              <>
                <Circle
                  center={[
                    userLocation.latitude,
                    userLocation.longitude,
                  ]}
                  radius={Math.max(
                    25,
                    Math.min(
                      userLocation.accuracy || 50,
                      250
                    )
                  )}
                  pathOptions={{
                    color: "#9fff72",
                    fillColor: "#9fff72",
                    fillOpacity: 0.07,
                    opacity: 0.3,
                    weight: 1,
                  }}
                />

                <Marker
                  position={[
                    userLocation.latitude,
                    userLocation.longitude,
                  ]}
                  icon={USER_MARKER}
                />
              </>
            )}

            {activeFilters.includes("places") &&
              filteredPlaces.map((place) => (
              <Marker
                key={place.id}
                position={[
                  Number(place.latitude),
                  Number(place.longitude),
                ]}
                icon={makePlaceMarker(
                  place,
                  place.id === selectedPlaceId
                )}
                zIndexOffset={
                  place.id === selectedPlaceId
                    ? 1000
                    : 0
                }
                eventHandlers={{
                  click: () => {
                    setSelectedPlaceId(place.id);
                    setSelectedHostId(null);
                    setResultsOpen(false);
                  },
                }}
              />
            ))}

            {filteredHosts.map((host) => (
              <Marker
                key={`host-${host.id}`}
                position={[
                  Number(host.latitude),
                  Number(host.longitude),
                ]}
                icon={makeHostMarker(
                  host,
                  host.id === selectedHostId
                )}
                zIndexOffset={
                  host.id === selectedHostId ? 1200 : 400
                }
                eventHandlers={{
                  click: () => {
                    setSelectedHostId(host.id);
                    setSelectedPlaceId(null);
                    setResultsOpen(false);
                  },
                }}
              />
            ))}
          </MapContainer>

          <div className="moMapShade" />
          <div className="moMapVignette" />

          <header className="moMapHeader">
            <div className="moBrand">
              <span>
                <Icon name="compass" size={18} />
              </span>

              <div>
                <strong>EXPLORE</strong>
                <small>
                  {userLocation
                    ? "Mapa oko tebe"
                    : "MeetOutdoors mapa"}
                </small>
              </div>
            </div>

            <div className="moHeaderActions">
              <button
                type="button"
                onClick={() =>
                  setLocateRequest(
                    (value) => value + 1
                  )
                }
              >
                <Icon name="locate" size={16} />

                <span>
                  {userLocation
                    ? "Moja lokacija"
                    : "Pronađi me"}
                </span>
              </button>

              <Link
                to={
                  profile
                    ? "/explore/add"
                    : "/login"
                }
                className="moHeaderDiscover"
              >
                <Icon name="camera" size={16} />
                <span>Otkrij mesto</span>
              </Link>
            </div>
          </header>

          <section
            className={`moCommand ${
              controlsOpen
                ? "open"
                : "collapsed"
            }`}
          >
            <div className="moCommandGlow" />

            <button
              type="button"
              className="moCommandCollapse"
              onClick={() =>
                setControlsOpen(
                  (value) => !value
                )
              }
              aria-label={
                controlsOpen
                  ? "Sakrij kontrole"
                  : "Prikaži kontrole"
              }
            >
              <Icon
                name={
                  controlsOpen
                    ? "chevronUp"
                    : "chevronDown"
                }
                size={17}
              />
            </button>

            <div className="moCommandIntro">
              <div className="moCommandBadge">
                <Icon
                  name="sparkle"
                  size={13}
                />

                <span>
                  {userLocation
                    ? "PRONAŠLI SMO TE"
                    : "LIVE OUTDOOR MAPA"}
                </span>
              </div>

              <h1>{userLocation ? "Istraži oko sebe" : "Istraži Srbiju"}</h1>

              <p>
                {userLocation
                  ? "Mesta i domaćini u tvojoj blizini."
                  : "Mesta, domaćini i outdoor ponude na jednoj mapi."}
              </p>
            </div>

            <label className="moSearch">
              <span className="moSearchIcon">
                <Icon name="search" size={17} />
              </span>

              <input
                value={query}
                onChange={(event) =>
                  setQuery(event.target.value)
                }
                placeholder="Tara, Uvac, jezero, vidikovac..."
              />

              {query && (
                <button
                  type="button"
                  onClick={() =>
                    setQuery("")
                  }
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </button>
              )}
            </label>

            <div className="moLayerFilters">
              {MAP_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={
                    activeFilters.includes(filter.id)
                      ? "active"
                      : ""
                  }
                  onClick={() => toggleMapFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {activeFilters.includes("services") && (
              <div className="moQuickFilters">
                <button
                  type="button"
                  className={serviceCategories.length === 0 ? "active" : ""}
                  onClick={() => setServiceCategories([])}
                >
                  Sve usluge
                </button>

                {SERVICE_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={
                      serviceCategories.includes(category)
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      toggleCategoryFilter(category, "service")
                    }
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {activeFilters.includes("rental") && (
              <div className="moQuickFilters">
                <button
                  type="button"
                  className={rentalCategories.length === 0 ? "active" : ""}
                  onClick={() => setRentalCategories([])}
                >
                  Sve za iznajmljivanje
                </button>

                {RENTAL_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={
                      rentalCategories.includes(category)
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      toggleCategoryFilter(category, "rental")
                    }
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {activeFilters.includes("places") && (
            <div className="moQuickFilters">
              <button
                type="button"
                className={
                  categoryId === "all"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setCategoryId("all")
                }
              >
                Sve
              </button>

              {categories
                .slice(0, 7)
                .map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={
                      categoryId ===
                      category.id
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setCategoryId(
                        category.id
                      )
                    }
                  >
                    {category.name}
                  </button>
                ))}
            </div>
            )}

            <div className="moNearMe">
              <div className="moNearMeHead">
                <div>
                  <span>
                    {userLocation
                      ? "NAJBLIŽE TEBI"
                      : "POPULARNO SADA"}
                  </span>

                  <small>
                    {userLocation
                      ? "po udaljenosti"
                      : "community izbor"}
                  </small>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setResultsOpen(true)
                  }
                >
                  Sva mesta
                  <Icon
                    name="arrow"
                    size={13}
                  />
                </button>
              </div>

              <div className="moNearMeList">
                {nearestPlaces.length === 0 ? (
                  <div className="moEmptyNearby">
                    Nema rezultata za ovaj filter.
                  </div>
                ) : (
                  nearestPlaces.map(
                    (place) => (
                      <button
                        key={place.id}
                        type="button"
                        className={
                          selectedPlaceId ===
                          place.id
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          setSelectedPlaceId(
                            place.id
                          )
                        }
                      >
                        <span className="moNearbyImage">
                          <img
                            src={getPlaceImage(
                              place
                            )}
                            alt=""
                          />
                        </span>

                        <div>
                          <span>
                            {place
                              .place_categories
                              ?.name ||
                              "Outdoor"}
                          </span>

                          <strong>
                            {place.name}
                          </strong>

                          <small>
                            {userLocation
                              ? formatDistance(
                                  place.distanceKm
                                )
                              : `${formatNumber(
                                  place.visitors_count
                                )} poseta`}
                          </small>
                        </div>

                        <span className="moNearArrow">
                          <Icon
                            name="arrow"
                            size={14}
                          />
                        </span>
                      </button>
                    )
                  )
                )}
              </div>
            </div>

            <div className="moCommandFooter">
              <div>
                <strong>
                  {formatNumber(
                    places.length
                  )}
                </strong>
                <span>mesta</span>
              </div>

              <div>
                <strong>
                  {formatNumber(
                    totals.checkins
                  )}
                </strong>
                <span>check-inova</span>
              </div>

              <button
                type="button"
                onClick={() =>
                  setResultsOpen(true)
                }
              >
                <Icon
                  name="grid"
                  size={15}
                />

                <span>Rezultati</span>

                <b>
                  {filteredPlaces.length}
                </b>
              </button>
            </div>
          </section>

          <aside className="moPassportHud">
            <span className="moPassportIcon">
              <Icon
                name="trophy"
                size={19}
              />
            </span>

            <div>
              <span>
                OUTDOOR PASSPORT
              </span>

              <strong>
                {profile
                  ? "Ne skupljaš lajkove. Skupljaš mesta."
                  : "Prijavi se i počni da otključavaš Srbiju."}
              </strong>
            </div>
          </aside>

          {selectedPlace && (
            <section className="moPlaceSheet">
              <div className="moPopupAccent" />
              <button
                type="button"
                className="moPlaceSheetClose"
                onClick={() =>
                  setSelectedPlaceId(null)
                }
              >
                <Icon
                  name="close"
                  size={15}
                />
              </button>

              <div className="moPlaceSheetVisual">
                <img
                  src={getPlaceImage(
                    selectedPlace
                  )}
                  alt=""
                />

                <div className="moPlaceSheetVisualShade" />

                <span>
                  {selectedPlace
                    .place_categories
                    ?.name ||
                    "Outdoor mesto"}
                </span>
              </div>

              <div className="moPlaceSheetBody">
                <div className="moPlaceSheetMeta">
                  <span>
                    <Icon
                      name="mapPin"
                      size={11}
                    />

                    {[selectedPlace.locality,
                      selectedPlace.region]
                      .filter(Boolean)
                      .join(" · ") ||
                      "Srbija"}
                  </span>

                  {selectedDistance != null && (
                    <em>
                      <Icon
                        name="navigation"
                        size={11}
                      />

                      {formatDistance(
                        selectedDistance
                      )}
                    </em>
                  )}
                </div>

                <h2>
                  {selectedPlace.name}
                </h2>

                {selectedPlace.short_description && (
                  <p className="moPlaceSheetDescription">
                    {
                      selectedPlace.short_description
                    }
                  </p>
                )}

                <div className="moPlaceSheetStats">
                  <span>
                    <Icon
                      name="users"
                      size={13}
                    />
                    {formatNumber(
                      selectedPlace.visitors_count
                    )}
                    <small>poseta</small>
                  </span>

                  <span>
                    <Icon
                      name="camera"
                      size={13}
                    />
                    {formatNumber(
                      selectedPlace.photos_count
                    )}
                    <small>slika</small>
                  </span>

                  <span>
                    <Icon
                      name="heart"
                      size={13}
                    />
                    {formatNumber(
                      selectedPlace.saves_count
                    )}
                    <small>
                      sačuvano
                    </small>
                  </span>
                </div>

                {selectedPlace.location_precision !==
                  "exact" && (
                  <div className="moProtectedNote">
                    Precizna lokacija je zaštićena.
                  </div>
                )}

                <div className="moPlaceSheetActions">
                  <button
                    type="button"
                    className="moNavigatePlace"
                    onClick={() =>
                      openNavigation(
                        selectedPlace
                      )
                    }
                  >
                    <span>
                      <Icon
                        name="route"
                        size={16}
                      />
                    </span>

                    <div>
                      <small>
                        GOOGLE MAPS
                      </small>
                      <strong>
                        Pokreni navigaciju
                      </strong>
                    </div>

                    <Icon
                      name="arrow"
                      size={15}
                    />
                  </button>

                  <button
                    type="button"
                    className="moOpenPlace"
                    onClick={() =>
                      navigate(
                        `/explore/${selectedPlace.id}`
                      )
                    }
                  >
                    <span>
                      Istraži mesto
                    </span>

                    <span className="moOpenPlaceArrow">
                      <Icon
                        name="arrow"
                        size={15}
                      />
                    </span>
                  </button>
                </div>
              </div>
            </section>
          )}

          {selectedHost && (
            <section className="moHostSheet">
              <div className="moPopupAccent" />

              <button
                type="button"
                className="moHostSheetClose"
                onClick={() => setSelectedHostId(null)}
                aria-label="Zatvori domaćina"
              >
                <Icon name="close" size={15} />
              </button>

              <div className="moHostSheetHead">
                <div className="moHostSheetAvatar">
                  {selectedHost.avatar_url ? (
                    <img src={selectedHost.avatar_url} alt="" />
                  ) : (
                    <strong>
                      {(
                        selectedHost.full_name ||
                        selectedHost.username ||
                        "H"
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </strong>
                  )}

                  <span className="moHostSheetVerified">
                    <Icon name="sparkle" size={11} />
                  </span>
                </div>

                <div className="moHostSheetIdentity">
                  <span>MEETOUTDOORS HOST</span>

                  <h2>
                    {selectedHost.full_name ||
                      selectedHost.username}
                  </h2>

                  <p>
                    <Icon name="mapPin" size={12} />
                    {selectedHost.public_location ||
                      [selectedHost.city, selectedHost.country]
                        .filter(Boolean)
                        .join(", ") ||
                      "Javna lokacija"}
                  </p>

                  {selectedHostDistance != null && (
                    <small>
                      <Icon name="navigation" size={11} />
                      {formatDistance(selectedHostDistance)} od tebe
                    </small>
                  )}
                </div>
              </div>

              <div className="moHostSheetDivider" />

              <div className="moHostCapabilities">
                {selectedHost.capabilities?.adventures && (
                  <span><Icon name="compass" size={12} />Avanture</span>
                )}
                {selectedHost.capabilities?.accommodation && (
                  <span><Icon name="mapPin" size={12} />Smeštaj</span>
                )}
                {selectedHost.capabilities?.services && (
                  <span><Icon name="sparkle" size={12} />Usluge</span>
                )}
                {selectedHost.capabilities?.rental && (
                  <span><Icon name="layers" size={12} />Iznajmljivanje</span>
                )}
              </div>

              <div className="moHostSheetFoot">
                <div>
                  <small>DIREKTAN KONTAKT</small>
                  <strong>Detalji i kontakt su na profilu domaćina.</strong>
                </div>

                <button
                  type="button"
                  className="moOpenHost"
                  onClick={() =>
                    navigate(`/h/${selectedHost.username}`)
                  }
                >
                  Otvori profil
                  <Icon name="arrow" size={15} />
                </button>
              </div>
            </section>
          )}

          <button
            type="button"
            className="moFloatingDiscover"
            onClick={() =>
              navigate(
                profile
                  ? "/explore/add"
                  : "/login"
              )
            }
          >
            <span className="moFloatingDiscoverIcon">
              <Icon
                name="camera"
                size={21}
              />
            </span>

            <span className="moFloatingDiscoverCopy">
              <small>
                NAŠAO SI NEŠTO?
              </small>
              <strong>
                Otkrij mesto
              </strong>
            </span>

            <span className="moFloatingDiscoverArrow">
              <Icon
                name="arrow"
                size={17}
              />
            </span>
          </button>

          {locateStatus !== "idle" && (
            <div
              className={`moLocateOverlay ${locateStatus}`}
            >
              <div className="moLocateRadar">
                <span />
                <i />
                <b />
                <Icon
                  name="navigation"
                  size={22}
                />
              </div>

              <div>
                <small>
                  MEETOUTDOORS GPS
                </small>
                <strong>
                  {locateMessage}
                </strong>

                <p>
                  {locateStatus ===
                    "locating" &&
                    "Tražimo tvoju poziciju i približavamo mapu."}

                  {locateStatus ===
                    "found" &&
                    "Mapa je sada centrirana na tvoju lokaciju."}

                  {locateStatus ===
                    "error" &&
                    "Mapa ostaje dostupna. GPS možeš pokušati ponovo."}
                </p>
              </div>
            </div>
          )}
        </div>

        <div
          className={`moResultsDrawer ${
            resultsOpen ? "open" : ""
          }`}
        >
          <button
            type="button"
            className="moDrawerBackdrop"
            onClick={() =>
              setResultsOpen(false)
            }
            aria-label="Zatvori rezultate"
          />

          <section>
            <header>
              <div>
                <span>
                  <Icon
                    name="filter"
                    size={15}
                  />

                  {userLocation
                    ? "SORTIRANO PO UDALJENOSTI"
                    : "REZULTATI"}
                </span>

                <h2>
                  {filteredPlaces.length} mesta
                </h2>

                <p>
                  Klikni na mesto i mapa će te odmah odvesti do njega.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setResultsOpen(false)
                }
              >
                <Icon
                  name="close"
                  size={18}
                />
              </button>
            </header>

            <div className="moDrawerList">
              {filteredPlaces.map(
                (place) => (
                  <button
                    key={place.id}
                    type="button"
                    className={
                      place.id ===
                      selectedPlaceId
                        ? "active"
                        : ""
                    }
                    onClick={() => {
                      setSelectedPlaceId(
                        place.id
                      );
                      setResultsOpen(
                        false
                      );
                    }}
                  >
                    <span className="moDrawerImage">
                      <img
                        src={getPlaceImage(
                          place
                        )}
                        alt=""
                      />
                    </span>

                    <div>
                      <span>
                        {place
                          .place_categories
                          ?.name ||
                          "Outdoor mesto"}
                      </span>

                      <strong>
                        {place.name}
                      </strong>

                      <small>
                        {userLocation
                          ? formatDistance(
                              place.distanceKm
                            )
                          : [
                              place.locality,
                              place.region,
                            ]
                              .filter(Boolean)
                              .join(" · ") ||
                            "Srbija"}
                      </small>
                    </div>

                    <span className="moDrawerArrow">
                      <Icon
                        name="arrow"
                        size={16}
                      />
                    </span>
                  </button>
                )
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}

function ExploreStyles() {
  return (
    <style>{`
      *{box-sizing:border-box}
      html,body,#root{min-height:100%}
      body{margin:0;background:#07110b}
      button,input{font:inherit}
      .moExplore{min-height:100vh;background:#07110b;color:#fff;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
      .moExplore a{color:inherit;text-decoration:none}
      .moMapStage{position:relative;height:100vh;min-height:720px;overflow:hidden;background:#dfe7dc}
      .moLeaflet{position:absolute;inset:0;z-index:1;width:100%;height:100%;background:#dfe7dc}
      .moMapShade{position:absolute;inset:0;z-index:2;pointer-events:none;background:linear-gradient(90deg,rgba(3,10,6,.76) 0%,rgba(3,10,6,.44) 23%,rgba(3,10,6,.07) 50%,transparent 72%),linear-gradient(180deg,rgba(3,10,6,.56),transparent 22%,transparent 72%,rgba(3,10,6,.38))}
      .moMapVignette{position:absolute;inset:0;z-index:2;pointer-events:none;box-shadow:inset 0 0 90px rgba(3,10,6,.18)}
      .moMapHeader{position:absolute;top:102px;right:22px;left:22px;z-index:700;display:flex;align-items:center;justify-content:space-between;gap:18px}
      .moBrand{display:flex;align-items:center;gap:10px;padding:9px 11px;border:1px solid rgba(255,255,255,.14);border-radius:17px;background:linear-gradient(145deg,rgba(4,14,8,.78),rgba(10,28,17,.62));box-shadow:0 16px 42px rgba(0,0,0,.22);backdrop-filter:blur(24px) saturate(140%)}
      .moBrand>span{display:grid;place-items:center;width:39px;height:39px;border:1px solid rgba(255,255,255,.35);border-radius:12px;background:#baff9e;color:#0e2717}
      .moBrand strong,.moBrand small{display:block}
      .moBrand strong{font-size:9px;letter-spacing:.14em}
      .moBrand small{margin-top:3px;color:rgba(255,255,255,.45);font-size:6px}
      .moHeaderActions{display:flex;gap:7px}
      .moHeaderActions button,.moHeaderActions a{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:43px;padding:0 13px;border:1px solid rgba(255,255,255,.14);border-radius:14px;background:linear-gradient(145deg,rgba(4,14,8,.74),rgba(12,30,19,.6));color:#fff;cursor:pointer;font-size:8px;font-weight:850;box-shadow:0 14px 34px rgba(0,0,0,.18);backdrop-filter:blur(22px)}
      .moHeaderActions .moHeaderDiscover{border-color:#baff9e;background:#baff9e;color:#102619}
      .moCommand{position:absolute;top:166px;left:22px;z-index:650;width:min(390px,calc(100% - 44px));padding:18px;overflow:hidden;border:1px solid rgba(255,255,255,.12);border-radius:26px;background:linear-gradient(145deg,rgba(4,14,8,.9),rgba(10,31,18,.79));box-shadow:0 28px 72px rgba(0,0,0,.31);backdrop-filter:blur(28px) saturate(140%)}
      .moCommandGlow{position:absolute;top:-90px;left:-70px;width:220px;height:220px;border-radius:50%;background:rgba(186,255,158,.08);filter:blur(54px);pointer-events:none}
      .moCommandCollapse{position:absolute;top:12px;right:12px;z-index:2;display:grid;place-items:center;width:33px;height:33px;border:1px solid rgba(255,255,255,.1);border-radius:11px;background:rgba(255,255,255,.06);color:#fff;cursor:pointer}
      .moCommandBadge{display:inline-flex;align-items:center;gap:6px;padding:6px 8px;border:1px solid rgba(186,255,158,.14);border-radius:999px;background:rgba(186,255,158,.06);color:#baff9e}
      .moCommandBadge span{font-size:6px;font-weight:950;letter-spacing:.12em}
      .moCommand h1{margin:13px 0 0;font-size:clamp(42px,5vw,60px);line-height:.84;letter-spacing:-.073em}
      .moCommand h1 em{color:#baff9e;font-style:normal}
      .moCommandIntro p{max-width:320px;margin:12px 0 0;color:rgba(255,255,255,.48);font-size:8px;line-height:1.58}
      .moSearch{display:flex;align-items:center;gap:8px;margin-top:16px;min-height:45px;padding:0 10px;border:1px solid rgba(255,255,255,.11);border-radius:13px;background:rgba(255,255,255,.06);color:rgba(255,255,255,.56)}
      .moSearchIcon{display:grid;place-items:center;width:28px;height:28px;border-radius:9px;background:rgba(255,255,255,.05)}
      .moSearch input{width:100%;min-width:0;border:0;outline:0;background:transparent;color:#fff;font-size:8px}
      .moSearch input::placeholder{color:rgba(255,255,255,.3)}
      .moSearch button{display:grid;place-items:center;width:27px;height:27px;border:0;border-radius:9px;background:rgba(255,255,255,.08);color:#fff;cursor:pointer}
      .moLayerFilters{display:flex;gap:7px;margin-top:10px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px}
      .moLayerFilters::-webkit-scrollbar{display:none}
      .moLayerFilters button{flex:0 0 auto;min-height:34px;padding:0 11px;border:1px solid rgba(255,255,255,.1);border-radius:999px;background:rgba(7,19,13,.62);backdrop-filter:blur(16px);color:rgba(255,255,255,.7);cursor:pointer;font-size:7px;font-weight:900;letter-spacing:.02em}
      .moLayerFilters button.active{border-color:#baff9e;background:#baff9e;color:#102619;box-shadow:0 7px 20px rgba(100,240,140,.12)}
      .moHostMarkerShell{background:transparent!important;border:0!important}
      .moHostMarker{position:relative;display:flex;align-items:center;gap:8px;width:max-content;max-width:180px;padding:6px 10px 8px 6px;border:1px solid rgba(186,255,158,.45);border-radius:17px;background:rgba(8,24,15,.94);box-shadow:0 12px 30px rgba(0,0,0,.28);backdrop-filter:blur(16px);transform:translateZ(0)}
      .moHostMarkerShell.active .moHostMarker{border-color:#baff9e;box-shadow:0 16px 40px rgba(0,0,0,.36),0 0 0 3px rgba(186,255,158,.12)}
      .moHostMarkerAvatar{width:34px;height:34px;border-radius:11px;display:grid;place-items:center;overflow:hidden;flex:0 0 auto;background:#baff9e;color:#14311f;font-size:12px;font-weight:900}
      .moHostMarkerAvatar img{width:100%;height:100%;object-fit:cover}
      .moHostMarkerLabel{min-width:0;display:flex;flex-direction:column;gap:1px}
      .moHostMarkerLabel small{font-size:6px;line-height:1;color:#baff9e;font-weight:900;letter-spacing:.12em}
      .moHostMarkerLabel strong{max-width:115px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:8px;line-height:1.2;color:#fff}
      .moHostMarker i{position:absolute;left:25px;bottom:-7px;width:12px;height:12px;background:rgba(8,24,15,.96);border-right:1px solid rgba(186,255,158,.45);border-bottom:1px solid rgba(186,255,158,.45);transform:rotate(45deg)}
      .moHostSheet{position:absolute;z-index:900;left:50%;bottom:28px;transform:translateX(-50%);width:min(420px,calc(100% - 28px));padding:18px;border:1px solid rgba(186,255,158,.18);border-radius:24px;background:rgba(8,22,14,.96);box-shadow:0 24px 70px rgba(0,0,0,.4);backdrop-filter:blur(24px);color:#fff}
      .moHostSheetClose{position:absolute;right:12px;top:12px;width:30px;height:30px;border:1px solid rgba(255,255,255,.1);border-radius:10px;background:rgba(255,255,255,.06);color:#fff;display:grid;place-items:center;cursor:pointer}
      .moHostSheetHead{display:flex;gap:12px;align-items:center;padding-right:34px}
      .moHostSheetAvatar{width:58px;height:58px;border-radius:18px;overflow:hidden;display:grid;place-items:center;flex:0 0 auto;background:#baff9e;color:#14311f;font-size:20px}
      .moHostSheetAvatar img{width:100%;height:100%;object-fit:cover}
      .moHostSheetHead>div:last-child{min-width:0}
      .moHostSheetHead span{font-size:7px;font-weight:900;letter-spacing:.14em;color:#baff9e}
      .moHostSheetHead h2{margin:4px 0 3px;font-size:18px;line-height:1.08}
      .moHostSheetHead p{margin:0;display:flex;align-items:center;gap:4px;font-size:10px;color:rgba(255,255,255,.6)}
      .moHostCapabilities{display:flex;gap:6px;flex-wrap:wrap;margin-top:14px}
      .moHostCapabilities span{padding:6px 8px;border-radius:999px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.08);font-size:8px;font-weight:800;color:rgba(255,255,255,.74)}
      .moOpenHost{margin-top:14px;width:100%;min-height:44px;border:0;border-radius:14px;background:#baff9e;color:#102619;display:flex;align-items:center;justify-content:center;gap:8px;font-size:10px;font-weight:900;cursor:pointer}
      .moQuickFilters{display:flex;gap:6px;margin-top:8px;overflow-x:auto;scrollbar-width:none}
      .moQuickFilters::-webkit-scrollbar{display:none}
      .moQuickFilters button{flex:0 0 auto;min-height:31px;padding:0 9px;border:1px solid rgba(255,255,255,.09);border-radius:999px;background:rgba(255,255,255,.045);color:rgba(255,255,255,.58);cursor:pointer;font-size:6px;font-weight:800}
      .moQuickFilters button.active{border-color:#baff9e;background:#baff9e;color:#102619}
      .moNearMe{margin-top:12px}
      .moNearMeHead{display:flex;align-items:flex-end;justify-content:space-between;gap:10px}
      .moNearMeHead>div>span,.moNearMeHead>div>small{display:block}
      .moNearMeHead>div>span{font-size:6px;font-weight:900;letter-spacing:.1em}
      .moNearMeHead>div>small{margin-top:2px;color:rgba(255,255,255,.27);font-size:5px}
      .moNearMeHead>button{display:inline-flex;align-items:center;gap:5px;border:0;background:transparent;color:#baff9e;cursor:pointer;font-size:6px;font-weight:850}
      .moNearMeList{display:grid;gap:5px;margin-top:7px}
      .moNearMeList>button{display:grid;grid-template-columns:44px minmax(0,1fr) 28px;align-items:center;gap:8px;width:100%;padding:5px;border:1px solid rgba(255,255,255,.04);border-radius:12px;background:rgba(255,255,255,.028);color:#fff;text-align:left;cursor:pointer}
      .moNearMeList>button.active{border-color:rgba(186,255,158,.18);background:rgba(186,255,158,.065)}
      .moNearbyImage{display:block;width:44px;height:44px;padding:2px;border:1px solid rgba(255,255,255,.08);border-radius:11px;background:rgba(255,255,255,.04)}
      .moNearMeList img{width:100%;height:100%;border-radius:8px;object-fit:cover}
      .moNearMeList>button>div>span,.moNearMeList strong,.moNearMeList small{display:block}
      .moNearMeList>button>div>span{color:#baff9e;font-size:5px;font-weight:900;text-transform:uppercase}
      .moNearMeList strong{margin-top:2px;overflow:hidden;font-size:7px;text-overflow:ellipsis;white-space:nowrap}
      .moNearMeList small{margin-top:2px;color:rgba(255,255,255,.36);font-size:5px}
      .moNearArrow{display:grid!important;place-items:center;width:27px;height:27px;border-radius:9px;background:rgba(255,255,255,.04);color:rgba(255,255,255,.55)}
      .moEmptyNearby{padding:13px;border:1px dashed rgba(255,255,255,.1);border-radius:12px;color:rgba(255,255,255,.4);font-size:7px;text-align:center}
      .moCommandFooter{display:grid;grid-template-columns:auto auto minmax(0,1fr);align-items:center;gap:8px;margin-top:10px;padding-top:10px;border-top:1px solid rgba(255,255,255,.08)}
      .moCommandFooter>div{min-width:60px}
      .moCommandFooter strong,.moCommandFooter span{display:block}
      .moCommandFooter strong{font-size:11px}
      .moCommandFooter>div span{margin-top:2px;color:rgba(255,255,255,.28);font-size:5px;text-transform:uppercase}
      .moCommandFooter>button{display:flex;align-items:center;justify-content:center;gap:6px;min-height:35px;border:1px solid rgba(186,255,158,.14);border-radius:10px;background:rgba(186,255,158,.07);color:#d9ffca;cursor:pointer;font-size:6px;font-weight:850}
      .moCommandFooter>button b{display:grid;place-items:center;min-width:21px;height:21px;padding:0 5px;border-radius:999px;background:#baff9e;color:#102619;font-size:6px}
      .moPassportHud{position:absolute;top:166px;right:22px;z-index:650;display:flex;align-items:center;gap:10px;max-width:286px;padding:10px 12px 10px 9px;border:1px solid rgba(255,255,255,.12);border-radius:16px;background:rgba(4,14,8,.7);color:#baff9e;backdrop-filter:blur(20px)}
      .moPassportIcon{display:grid;place-items:center;width:36px;height:36px;border-radius:11px;background:rgba(186,255,158,.08)}
      .moPassportHud>div>span,.moPassportHud strong{display:block}
      .moPassportHud>div>span{font-size:5px;font-weight:900;letter-spacing:.1em}
      .moPassportHud strong{margin-top:3px;color:#fff;font-size:7px;line-height:1.35}

      .moStickerMarkerShell,.moExpandedMarkerShell{background:transparent!important;border:0!important}
      .moStickerMarker{position:relative;display:flex;align-items:center;justify-content:center;gap:6px;min-width:76px;max-width:112px;height:34px;padding:0 9px;border:2px solid rgba(255,255,255,.94);border-radius:999px;background:#173b27;color:#fff;box-shadow:0 10px 22px rgba(0,0,0,.26);white-space:nowrap;transform:translateY(-2px);transition:transform .16s ease}
      .moStickerMarker:hover{transform:translateY(-4px) scale(1.03)}
      .moStickerMarker strong{max-width:72px;overflow:hidden;font-size:6px;font-weight:950;text-overflow:ellipsis;text-transform:uppercase;white-space:nowrap}
      .moStickerDot{width:7px;height:7px;border-radius:50%;background:#baff9e;box-shadow:0 0 0 4px rgba(186,255,158,.09)}
      .moStickerMarker>b{display:grid;place-items:center;min-width:18px;height:18px;padding:0 4px;border-radius:999px;background:#baff9e;color:#102619;font-size:5px}
      .moStickerMarker>i{position:absolute;bottom:-6px;width:12px;height:12px;border-right:2px solid rgba(255,255,255,.94);border-bottom:2px solid rgba(255,255,255,.94);background:#173b27;transform:rotate(45deg);z-index:-1}

      .moExpandedMarker{position:relative;display:grid;place-items:center;width:146px;height:156px;filter:drop-shadow(0 20px 30px rgba(0,0,0,.34))}
      .moExpandedHalo{position:absolute;inset:8px 8px 20px;border:1px solid rgba(186,255,158,.34);border-radius:26px;box-shadow:0 0 0 7px rgba(186,255,158,.06)}
      .moExpandedCard{position:relative;width:134px;height:122px;overflow:hidden;border:3px solid #baff9e;border-radius:25px;background:#173b27}
      .moExpandedCard>img{width:100%;height:100%;object-fit:cover}
      .moExpandedShade{position:absolute;inset:0;background:linear-gradient(180deg,rgba(4,14,8,.05),rgba(4,14,8,.08) 40%,rgba(4,14,8,.84))}
      .moExpandedTop{position:absolute;top:7px;right:7px;left:7px;display:flex;align-items:center;justify-content:space-between;gap:6px}
      .moExpandedTop>span{max-width:92px;padding:5px 6px;overflow:hidden;border-radius:999px;background:rgba(4,14,8,.62);color:#dfffd1;font-size:5px;font-weight:950;text-overflow:ellipsis;text-transform:uppercase;white-space:nowrap;backdrop-filter:blur(8px)}
      .moExpandedTop>b{display:grid;place-items:center;min-width:22px;height:22px;padding:0 5px;border:1px solid rgba(255,255,255,.35);border-radius:999px;background:#baff9e;color:#102619;font-size:5px}
      .moExpandedBottom{position:absolute;right:8px;bottom:8px;left:8px}
      .moExpandedBottom strong,.moExpandedBottom small{display:block}
      .moExpandedBottom strong{overflow:hidden;color:#fff;font-size:8px;font-weight:950;text-overflow:ellipsis;white-space:nowrap;text-shadow:0 2px 8px rgba(0,0,0,.75)}
      .moExpandedBottom small{margin-top:2px;color:rgba(255,255,255,.55);font-size:4px}
      .moExpandedTip{position:absolute;bottom:5px;width:22px;height:22px;border-right:3px solid #baff9e;border-bottom:3px solid #baff9e;background:#173b27;transform:rotate(45deg);z-index:-1}

      .moUserMarkerShell{background:transparent!important;border:0!important}
      .moUserMarker{position:relative;display:grid;place-items:center;width:34px;height:34px}
      .moUserMarker>span{position:absolute;inset:8px;border:3px solid #fff;border-radius:50%;background:#8fff68;box-shadow:0 0 0 4px rgba(143,255,104,.24),0 6px 20px rgba(0,0,0,.28)}
      .moUserMarker>i{position:absolute;inset:0;border:1px solid rgba(143,255,104,.75);border-radius:50%;animation:moUserPulse 1.8s ease-out infinite}
      @keyframes moUserPulse{0%{opacity:.9;transform:scale(.5)}100%{opacity:0;transform:scale(1.45)}}

      .moPlaceSheet{position:absolute;right:22px;bottom:22px;z-index:650;display:grid;grid-template-columns:150px minmax(0,1fr);width:min(500px,calc(100% - 44px));overflow:hidden;border:1px solid rgba(255,255,255,.13);border-radius:23px;background:linear-gradient(145deg,rgba(5,16,9,.94),rgba(12,31,18,.89));box-shadow:0 30px 78px rgba(0,0,0,.35);backdrop-filter:blur(26px);animation:moSheetIn .22s ease both}
      @keyframes moSheetIn{from{opacity:0;transform:translateY(12px) scale(.985)}to{opacity:1;transform:translateY(0) scale(1)}}
      .moPlaceSheetVisual{position:relative;min-height:218px;overflow:hidden}
      .moPlaceSheetVisual>img{width:100%;height:100%;object-fit:cover}
      .moPlaceSheetVisualShade{position:absolute;inset:0;background:linear-gradient(180deg,transparent 42%,rgba(5,16,9,.76))}
      .moPlaceSheetVisual>span{position:absolute;right:9px;bottom:9px;left:9px;padding:6px 8px;border:1px solid rgba(255,255,255,.13);border-radius:9px;background:rgba(5,16,9,.54);color:#baff9e;font-size:5px;font-weight:900;text-transform:uppercase;backdrop-filter:blur(10px)}
      .moPlaceSheetBody{padding:14px}
      .moPlaceSheetClose{position:absolute;top:8px;right:8px;z-index:3;display:grid;place-items:center;width:30px;height:30px;border:1px solid rgba(255,255,255,.13);border-radius:9px;background:rgba(3,10,6,.62);color:#fff;cursor:pointer}
      .moPlaceSheetMeta{display:flex;align-items:center;justify-content:space-between;gap:8px;padding-right:26px}
      .moPlaceSheetMeta>span{display:inline-flex;align-items:center;gap:4px;max-width:68%;overflow:hidden;color:rgba(255,255,255,.44);font-size:5px;text-overflow:ellipsis;white-space:nowrap}
      .moPlaceSheetMeta em{display:inline-flex;align-items:center;gap:4px;color:#d7ffc6;font-size:5px;font-style:normal}
      .moPlaceSheet h2{margin:8px 0 0;font-size:21px;line-height:1.03;letter-spacing:-.05em}
      .moPlaceSheetDescription{display:-webkit-box;margin:7px 0 0;overflow:hidden;color:rgba(255,255,255,.46);font-size:6px;line-height:1.45;-webkit-line-clamp:2;-webkit-box-orient:vertical}
      .moPlaceSheetStats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:5px;margin-top:10px}
      .moPlaceSheetStats>span{display:grid;grid-template-columns:auto 1fr;align-items:center;column-gap:4px;padding:7px;border:1px solid rgba(255,255,255,.07);border-radius:9px;background:rgba(255,255,255,.035);color:#fff;font-size:6px}
      .moPlaceSheetStats small{grid-column:2;color:rgba(255,255,255,.28);font-size:4px}
      .moProtectedNote{margin-top:8px;padding:7px 8px;border:1px solid rgba(229,211,146,.12);border-radius:9px;background:rgba(229,211,146,.08);color:#e8da9f;font-size:5px}
      .moPlaceSheetActions{display:grid;grid-template-columns:1.2fr .8fr;gap:7px;margin-top:10px}
      .moNavigatePlace{display:grid;grid-template-columns:32px minmax(0,1fr) auto;align-items:center;gap:7px;min-height:43px;padding:5px 9px 5px 5px;border:1px solid rgba(186,255,158,.22);border-radius:11px;background:#173b27;color:#fff;cursor:pointer}
      .moNavigatePlace>span{display:grid;place-items:center;width:32px;height:32px;border-radius:9px;background:rgba(186,255,158,.1);color:#baff9e}
      .moNavigatePlace small,.moNavigatePlace strong{display:block;text-align:left}
      .moNavigatePlace small{color:rgba(255,255,255,.34);font-size:4px;font-weight:900;letter-spacing:.07em}
      .moNavigatePlace strong{margin-top:2px;font-size:6px}
      .moOpenPlace{display:grid;grid-template-columns:1fr auto;align-items:center;gap:7px;min-height:43px;padding:0 6px 0 10px;border:0;border-radius:11px;background:#baff9e;color:#102619;cursor:pointer;font-size:7px;font-weight:900}
      .moOpenPlaceArrow{display:grid;place-items:center;width:30px;height:30px;border-radius:9px;background:rgba(16,38,25,.08)}
      .moFloatingDiscover{position:absolute;right:22px;bottom:252px;z-index:650;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:9px;min-width:205px;min-height:54px;padding:6px 8px 6px 6px;border:1px solid rgba(186,255,158,.26);border-radius:17px;background:rgba(8,28,15,.92);color:#fff;cursor:pointer;box-shadow:0 22px 50px rgba(0,0,0,.28);backdrop-filter:blur(20px)}
      .moFloatingDiscoverIcon{display:grid;place-items:center;width:40px;height:40px;border-radius:12px;background:#baff9e;color:#102619}
      .moFloatingDiscoverCopy small,.moFloatingDiscoverCopy strong{display:block;text-align:left}
      .moFloatingDiscoverCopy small{color:#baff9e;font-size:4px;font-weight:900;letter-spacing:.08em}
      .moFloatingDiscoverCopy strong{margin-top:2px;font-size:7px}
      .moFloatingDiscoverArrow{display:grid;place-items:center;width:29px;height:29px;border-radius:9px;background:rgba(255,255,255,.05);color:#baff9e}
      .moLocateOverlay{position:absolute;top:50%;left:50%;z-index:1500;display:flex;align-items:center;gap:15px;width:min(380px,calc(100% - 34px));padding:16px;border:1px solid rgba(255,255,255,.13);border-radius:22px;background:rgba(4,14,8,.92);box-shadow:0 32px 80px rgba(0,0,0,.42);transform:translate(-50%,-50%);backdrop-filter:blur(28px)}
      .moLocateOverlay small,.moLocateOverlay strong{display:block}
      .moLocateOverlay small{color:#baff9e;font-size:6px;font-weight:900;letter-spacing:.11em}
      .moLocateOverlay strong{margin-top:4px;font-size:16px}
      .moLocateOverlay p{margin:5px 0 0;color:rgba(255,255,255,.44);font-size:7px;line-height:1.45}
      .moLocateRadar{position:relative;display:grid;place-items:center;flex:0 0 auto;width:66px;height:66px;border-radius:20px;background:rgba(186,255,158,.08);color:#baff9e}
      .moLocateRadar>span,.moLocateRadar>i,.moLocateRadar>b{position:absolute;border:1px solid rgba(186,255,158,.24);border-radius:50%;animation:moRadar 1.8s ease-out infinite}
      .moLocateRadar>span{inset:10px}
      .moLocateRadar>i{inset:4px;animation-delay:.35s}
      .moLocateRadar>b{inset:-2px;animation-delay:.7s}
      @keyframes moRadar{0%{opacity:.7;transform:scale(.55)}100%{opacity:0;transform:scale(1.15)}}
      .moResultsDrawer{position:fixed;inset:0;z-index:5000;pointer-events:none}
      .moResultsDrawer.open{pointer-events:auto}
      .moDrawerBackdrop{position:absolute;inset:0;border:0;background:rgba(0,0,0,.62);opacity:0;transition:.24s ease;backdrop-filter:blur(4px)}
      .moResultsDrawer.open .moDrawerBackdrop{opacity:1}
      .moResultsDrawer>section{position:absolute;top:0;bottom:0;left:0;width:min(500px,94vw);padding:116px 18px 18px;background:linear-gradient(180deg,#08140d,#0b1d12);color:#fff;box-shadow:28px 0 80px rgba(0,0,0,.4);transform:translateX(-100%);transition:.3s cubic-bezier(.2,.7,.2,1)}
      .moResultsDrawer.open>section{transform:translateX(0)}
      .moResultsDrawer header{display:flex;align-items:flex-start;justify-content:space-between;gap:15px;padding:0 4px 15px;border-bottom:1px solid rgba(255,255,255,.1)}
      .moResultsDrawer header span{display:inline-flex;align-items:center;gap:6px;color:#baff9e;font-size:7px;font-weight:900;letter-spacing:.1em}
      .moResultsDrawer header h2{margin:7px 0 0;font-size:28px;letter-spacing:-.05em}
      .moResultsDrawer header p{max-width:360px;margin:5px 0 0;color:rgba(255,255,255,.36);font-size:7px;line-height:1.45}
      .moResultsDrawer header>button{display:grid;place-items:center;width:38px;height:38px;border:1px solid rgba(255,255,255,.11);border-radius:12px;background:rgba(255,255,255,.06);color:#fff;cursor:pointer}
      .moDrawerList{display:grid;gap:8px;height:calc(100vh - 220px);margin-top:12px;overflow-y:auto;padding-right:2px}
      .moDrawerList>button{display:grid;grid-template-columns:76px minmax(0,1fr) 32px;align-items:center;gap:10px;width:100%;padding:7px;border:1px solid rgba(255,255,255,.075);border-radius:15px;background:rgba(255,255,255,.035);color:#fff;text-align:left;cursor:pointer}
      .moDrawerList>button.active{border-color:rgba(186,255,158,.28);background:rgba(186,255,158,.07)}
      .moDrawerImage{display:block;width:76px;height:66px;padding:2px;border:1px solid rgba(255,255,255,.08);border-radius:11px;background:rgba(255,255,255,.03)}
      .moDrawerList img{width:100%;height:100%;border-radius:8px;object-fit:cover}
      .moDrawerList>button>div>span,.moDrawerList strong,.moDrawerList small{display:block}
      .moDrawerList>button>div>span{color:#baff9e;font-size:5px;font-weight:900;text-transform:uppercase}
      .moDrawerList strong{margin-top:3px;overflow:hidden;font-size:8px;text-overflow:ellipsis;white-space:nowrap}
      .moDrawerList small{margin-top:3px;color:rgba(255,255,255,.4);font-size:5px}
      .moDrawerArrow{display:grid!important;place-items:center;width:30px;height:30px;border-radius:9px;background:rgba(255,255,255,.04);color:#baff9e}
      .moLoading{display:grid;place-items:center;align-content:center;gap:10px;min-height:100vh;background:radial-gradient(circle at 50% 42%,rgba(186,255,158,.08),transparent 20%),#07110b;color:#fff;font-family:Inter,system-ui,sans-serif;text-align:center}
      .moLoadingOrb{display:grid;place-items:center;width:62px;height:62px;border:1px solid rgba(186,255,158,.16);border-radius:20px;background:rgba(186,255,158,.07);color:#baff9e}
      .moLoading strong{font-size:15px}
      .moLoading>span{color:rgba(255,255,255,.38);font-size:8px}

      @media(max-width:900px){
        .moPassportHud{display:none}
        .moCommand{top:156px}
        .moMapHeader{top:88px}
      }

      @media(max-width:700px){
        .moMapStage{height:100svh;min-height:650px}
        .moMapHeader{top:78px;right:10px;left:10px}
        .moBrand{padding:7px 8px;border-radius:14px}
        .moBrand>span{width:34px;height:34px}
        .moBrand small{display:none}
        .moHeaderActions{gap:5px}
        .moHeaderActions button,.moHeaderActions a{width:40px;min-height:40px;padding:0;border-radius:12px}
        .moHeaderActions span{display:none}
        .moCommand{top:auto;right:10px;bottom:10px;left:10px;width:auto;max-height:52vh;padding:14px;border-radius:20px;overflow-y:auto}
        .moCommand.collapsed{right:auto;bottom:12px;width:170px;max-height:none;padding:11px}
        .moCommand.collapsed .moCommandIntro p,
        .moCommand.collapsed .moSearch,
        .moCommand.collapsed .moLayerFilters,
        .moCommand.collapsed .moQuickFilters,
        .moCommand.collapsed .moNearMe,
        .moCommand.collapsed .moCommandFooter{display:none}
        .moCommand.collapsed .moCommandIntro h1{margin-top:8px;font-size:26px;line-height:.9}
        .moCommand h1{font-size:36px}
        .moNearMeList{grid-template-columns:repeat(2,minmax(0,1fr))}
        .moNearMeList>button{grid-template-columns:40px minmax(0,1fr)}
        .moNearArrow{display:none!important}
        .moNearbyImage{width:40px;height:40px}
        .moFloatingDiscover{right:10px;bottom:auto;top:130px;min-width:0;min-height:48px;padding:5px;border-radius:15px}
        .moFloatingDiscoverCopy,.moFloatingDiscoverArrow{display:none}
        .moFloatingDiscoverIcon{width:38px;height:38px}
        .moPlaceSheet{right:10px;bottom:10px;left:10px;grid-template-columns:108px minmax(0,1fr);width:auto;border-radius:19px}
        .moPlaceSheetVisual{min-height:188px}
        .moPlaceSheet h2{font-size:17px}
        .moPlaceSheetBody{padding:11px}
        .moPlaceSheetDescription{display:none}
        .moPlaceSheetStats{gap:4px}
        .moPlaceSheetStats>span{padding:5px}
        .moPlaceSheetActions{grid-template-columns:1fr}
        .moPlaceSheet+.moFloatingDiscover{display:none}
        .moStickerMarker{height:31px;min-width:70px;max-width:96px;padding:0 7px}
        .moStickerMarker strong{max-width:62px;font-size:5px}
        .moExpandedMarker{width:126px;height:138px}
        .moExpandedCard{width:116px;height:106px;border-radius:22px}
        .moExpandedBottom strong{font-size:7px}
        .moResultsDrawer>section{padding-top:96px}
        .moDrawerList{height:calc(100vh - 195px)}
      }

      @media(max-width:470px){
        .moCommand{max-height:58vh}
        .moNearMeList{grid-template-columns:1fr}
        .moCommandFooter{grid-template-columns:auto auto minmax(96px,1fr)}
        .moPlaceSheet{grid-template-columns:96px minmax(0,1fr)}
        .moPlaceSheetVisual{min-height:180px}
        .moPlaceSheetStats small{display:none}
      }

      @media(prefers-reduced-motion:reduce){
        *,*::before,*::after{
          animation:none!important;
          transition:none!important;
          scroll-behavior:auto!important
        }
      }

      /* =========================================================
         PREMIUM MAP MARKERS + SELECTED POPUPS
         Small at rest. Rich only after selection.
         ========================================================= */

      .moPlacePinShell,
      .moHostAvatarPinShell{
        background:transparent!important;
        border:0!important;
      }

      .moPlacePin{
        position:relative;
        width:54px;
        height:58px;
        display:grid;
        place-items:start center;
        cursor:pointer;
        filter:drop-shadow(0 12px 18px rgba(8,22,14,.28));
        transition:transform .18s ease,filter .18s ease;
      }

      .moPlacePin:hover{
        transform:translateY(-3px) scale(1.04);
        filter:drop-shadow(0 16px 24px rgba(8,22,14,.34));
      }

      .moPlacePinCore{
        position:relative;
        z-index:2;
        width:42px;
        height:42px;
        display:grid;
        place-items:center;
        border:3px solid rgba(255,255,255,.98);
        border-radius:16px 16px 16px 6px;
        background:linear-gradient(145deg,#1d4a33,#123422);
        color:#dffff0;
        box-shadow:
          0 8px 22px rgba(8,22,14,.25),
          inset 0 1px 0 rgba(255,255,255,.14);
        transform:rotate(-45deg);
      }

      .moPlacePinCore svg{
        width:20px;
        height:20px;
        fill:none;
        stroke:currentColor;
        stroke-width:2;
        stroke-linecap:round;
        stroke-linejoin:round;
        transform:rotate(45deg);
      }

      .moPlacePinCount{
        position:absolute;
        z-index:5;
        top:-5px;
        right:-2px;
        min-width:20px;
        height:20px;
        padding:0 5px;
        display:grid;
        place-items:center;
        border:2px solid #fff;
        border-radius:999px;
        background:#cfffbb;
        color:#14311f;
        font-size:7px;
        font-weight:950;
        box-shadow:0 5px 12px rgba(0,0,0,.18);
      }

      .moPlacePinLabel{
        position:absolute;
        z-index:3;
        top:39px;
        left:50%;
        max-width:96px;
        transform:translateX(-50%);
        padding:4px 7px;
        overflow:hidden;
        border:1px solid rgba(255,255,255,.24);
        border-radius:999px;
        background:rgba(8,24,15,.88);
        color:#fff;
        backdrop-filter:blur(10px);
        font-size:6px;
        font-weight:900;
        letter-spacing:.04em;
        text-overflow:ellipsis;
        text-transform:uppercase;
        white-space:nowrap;
        box-shadow:0 6px 16px rgba(0,0,0,.18);
      }

      .moPlacePinShell.active .moPlacePin{
        transform:translateY(-7px) scale(1.14);
        filter:drop-shadow(0 20px 30px rgba(8,22,14,.4));
      }

      .moPlacePinShell.active .moPlacePinCore{
        background:linear-gradient(145deg,#cfffbb,#aef48f);
        color:#14311f;
        border-color:#fff;
        box-shadow:
          0 0 0 6px rgba(186,255,158,.13),
          0 12px 28px rgba(0,0,0,.28);
      }

      .moPlacePinShell.active .moPlacePinLabel{
        background:#102c1d;
        border-color:rgba(207,255,187,.45);
        color:#dffff0;
      }

      .moHostAvatarPin{
        position:relative;
        width:56px;
        height:58px;
        display:grid;
        place-items:start center;
        cursor:pointer;
        transition:transform .18s ease,filter .18s ease;
        filter:drop-shadow(0 12px 18px rgba(7,18,11,.3));
      }

      .moHostAvatarPin:hover{
        transform:translateY(-3px) scale(1.04);
      }

      .moHostAvatarPinImage{
        position:relative;
        z-index:2;
        width:46px;
        height:46px;
        overflow:hidden;
        display:grid;
        place-items:center;
        border:3px solid #fff;
        border-radius:50%;
        background:linear-gradient(145deg,#cfffbb,#9de87f);
        color:#14311f;
        font-size:14px;
        font-weight:950;
        box-shadow:
          0 0 0 3px rgba(23,63,44,.90),
          0 10px 26px rgba(0,0,0,.26);
      }

      .moHostAvatarPinImage img{
        width:100%;
        height:100%;
        object-fit:cover;
      }

      .moHostVerifiedDot{
        position:absolute;
        z-index:5;
        right:0;
        top:28px;
        width:19px;
        height:19px;
        display:grid;
        place-items:center;
        border:2px solid #fff;
        border-radius:50%;
        background:#baff9e;
        color:#14311f;
        box-shadow:0 5px 12px rgba(0,0,0,.2);
      }

      .moHostVerifiedDot svg{
        width:11px;
        height:11px;
        fill:none;
        stroke:currentColor;
        stroke-width:2.5;
        stroke-linecap:round;
        stroke-linejoin:round;
      }

      .moHostCapabilityCount{
        position:absolute;
        z-index:5;
        left:-3px;
        top:-3px;
        min-width:19px;
        height:19px;
        padding:0 4px;
        display:grid;
        place-items:center;
        border:2px solid #fff;
        border-radius:999px;
        background:#173f2c;
        color:#dffff0;
        font-size:7px;
        font-weight:950;
      }

      .moHostAvatarPin>i{
        position:absolute;
        z-index:1;
        top:37px;
        width:15px;
        height:15px;
        border-right:3px solid #fff;
        border-bottom:3px solid #fff;
        background:#173f2c;
        transform:rotate(45deg);
      }

      .moHostAvatarPinShell.active .moHostAvatarPin{
        transform:translateY(-6px) scale(1.13);
      }

      .moHostAvatarPinShell.active .moHostAvatarPinImage{
        box-shadow:
          0 0 0 4px #baff9e,
          0 0 0 9px rgba(186,255,158,.13),
          0 16px 34px rgba(0,0,0,.34);
      }

      /* Selected cards: one visual language for places + hosts. */
      .moPopupAccent{
        position:absolute;
        z-index:8;
        top:0;
        left:30px;
        right:30px;
        height:2px;
        background:linear-gradient(90deg,transparent,#baff9e,transparent);
        opacity:.85;
        pointer-events:none;
      }

      .moPlaceSheet,
      .moHostSheet{
        border:1px solid rgba(255,255,255,.13)!important;
        background:
          radial-gradient(circle at 10% 0%,rgba(186,255,158,.07),transparent 19rem),
          linear-gradient(145deg,rgba(7,20,12,.96),rgba(13,33,20,.94))!important;
        box-shadow:
          0 34px 90px rgba(0,0,0,.42),
          inset 0 1px 0 rgba(255,255,255,.05)!important;
        backdrop-filter:blur(28px) saturate(1.14)!important;
      }

      .moPlaceSheet{
        right:24px!important;
        bottom:24px!important;
        grid-template-columns:168px minmax(0,1fr)!important;
        width:min(550px,calc(100% - 48px))!important;
        border-radius:27px!important;
      }

      .moPlaceSheetVisual{
        min-height:228px!important;
      }

      .moPlaceSheetVisual>span{
        right:12px!important;
        bottom:12px!important;
        left:12px!important;
        padding:7px 9px!important;
        border-radius:11px!important;
        color:#dffff0!important;
        font-size:7px!important;
        letter-spacing:.08em!important;
      }

      .moPlaceSheetBody{
        padding:18px 18px 17px!important;
      }

      .moPlaceSheetClose,
      .moHostSheetClose{
        width:34px!important;
        height:34px!important;
        border-radius:11px!important;
        background:rgba(255,255,255,.065)!important;
        transition:background .18s ease,transform .18s ease;
      }

      .moPlaceSheetClose:hover,
      .moHostSheetClose:hover{
        background:rgba(255,255,255,.12)!important;
        transform:scale(1.04);
      }

      .moPlaceSheetMeta>span,
      .moPlaceSheetMeta em{
        font-size:8px!important;
      }

      .moPlaceSheet h2{
        margin:9px 0 0!important;
        font-size:27px!important;
        line-height:.98!important;
        letter-spacing:-.05em!important;
        text-wrap:balance;
      }

      .moPlaceSheetDescription{
        margin-top:9px!important;
        color:rgba(255,255,255,.56)!important;
        font-size:9px!important;
        line-height:1.55!important;
        -webkit-line-clamp:3!important;
      }

      .moPlaceSheetStats{
        gap:7px!important;
        margin-top:13px!important;
      }

      .moPlaceSheetStats>span{
        padding:9px!important;
        border-radius:11px!important;
        font-size:8px!important;
      }

      .moPlaceSheetStats small{
        font-size:6px!important;
      }

      .moProtectedNote{
        padding:8px 10px!important;
        border-radius:11px!important;
        font-size:7px!important;
      }

      .moPlaceSheetActions{
        gap:8px!important;
        margin-top:12px!important;
      }

      .moNavigatePlace,
      .moOpenPlace{
        min-height:48px!important;
        border-radius:13px!important;
      }

      .moNavigatePlace small{
        font-size:5px!important;
      }

      .moNavigatePlace strong,
      .moOpenPlace{
        font-size:8px!important;
      }

      .moHostSheet{
        left:50%!important;
        bottom:24px!important;
        width:min(470px,calc(100% - 40px))!important;
        padding:20px!important;
        border-radius:27px!important;
        animation:moHostPopIn .22s ease both;
      }

      @keyframes moHostPopIn{
        from{opacity:0;transform:translate(-50%,12px) scale(.985)}
        to{opacity:1;transform:translate(-50%,0) scale(1)}
      }

      .moHostSheetHead{
        gap:14px!important;
        padding-right:38px!important;
      }

      .moHostSheetAvatar{
        position:relative;
        width:70px!important;
        height:70px!important;
        border:3px solid rgba(255,255,255,.92);
        border-radius:22px!important;
        background:#baff9e!important;
        box-shadow:0 12px 28px rgba(0,0,0,.24);
      }

      .moHostSheetVerified{
        position:absolute;
        right:-5px;
        bottom:-5px;
        width:24px;
        height:24px;
        display:grid!important;
        place-items:center;
        border:2px solid #102619;
        border-radius:50%;
        background:#baff9e;
        color:#14311f!important;
      }

      .moHostSheetIdentity{
        min-width:0;
      }

      .moHostSheetIdentity>span{
        color:#baff9e!important;
        font-size:7px!important;
        letter-spacing:.14em!important;
      }

      .moHostSheetHead h2{
        margin:5px 0 5px!important;
        font-size:24px!important;
        line-height:1!important;
        letter-spacing:-.04em;
      }

      .moHostSheetHead p{
        color:rgba(255,255,255,.59)!important;
        font-size:10px!important;
      }

      .moHostSheetIdentity>small{
        display:flex;
        align-items:center;
        gap:5px;
        margin-top:5px;
        color:#d9ffc8;
        font-size:8px;
        font-weight:800;
      }

      .moHostSheetDivider{
        height:1px;
        margin:16px 0 13px;
        background:linear-gradient(90deg,rgba(255,255,255,.11),rgba(255,255,255,.025));
      }

      .moHostCapabilities{
        gap:7px!important;
        margin-top:0!important;
      }

      .moHostCapabilities span{
        display:inline-flex!important;
        align-items:center!important;
        gap:5px!important;
        padding:7px 9px!important;
        border-color:rgba(186,255,158,.10)!important;
        border-radius:999px!important;
        background:rgba(186,255,158,.055)!important;
        color:rgba(239,255,233,.82)!important;
        font-size:8px!important;
      }

      .moHostSheetFoot{
        display:grid;
        grid-template-columns:minmax(0,1fr) auto;
        align-items:center;
        gap:14px;
        margin-top:16px;
      }

      .moHostSheetFoot>div small,
      .moHostSheetFoot>div strong{
        display:block;
      }

      .moHostSheetFoot>div small{
        color:rgba(255,255,255,.32);
        font-size:6px;
        font-weight:900;
        letter-spacing:.1em;
      }

      .moHostSheetFoot>div strong{
        margin-top:3px;
        color:rgba(255,255,255,.72);
        font-size:8px;
        line-height:1.35;
      }

      .moOpenHost{
        width:auto!important;
        min-width:132px;
        min-height:46px!important;
        margin-top:0!important;
        padding:0 15px!important;
        border-radius:13px!important;
        background:#baff9e!important;
        color:#102619!important;
        font-size:9px!important;
        box-shadow:0 10px 26px rgba(186,255,158,.12);
      }

      @media(max-width:700px){
        .moPlacePinLabel{
          display:none;
        }

        .moPlacePin{
          width:48px;
          height:52px;
        }

        .moPlacePinCore{
          width:38px;
          height:38px;
          border-radius:14px 14px 14px 5px;
        }

        .moHostAvatarPin{
          width:50px;
          height:54px;
        }

        .moHostAvatarPinImage{
          width:42px;
          height:42px;
        }

        .moPlaceSheet,
        .moHostSheet{
          right:10px!important;
          bottom:10px!important;
          left:10px!important;
          width:auto!important;
          transform:none!important;
        }

        .moPlaceSheet{
          grid-template-columns:112px minmax(0,1fr)!important;
          border-radius:21px!important;
        }

        .moPlaceSheetVisual{
          min-height:190px!important;
        }

        .moPlaceSheetBody{
          padding:13px!important;
        }

        .moPlaceSheet h2{
          font-size:20px!important;
        }

        .moPlaceSheetDescription{
          display:none!important;
        }

        .moPlaceSheetStats{
          gap:4px!important;
        }

        .moPlaceSheetStats>span{
          padding:6px!important;
          font-size:7px!important;
        }

        .moPlaceSheetStats small{
          display:none;
        }

        .moPlaceSheetActions{
          grid-template-columns:1fr!important;
        }

        .moNavigatePlace{
          display:none!important;
        }

        .moHostSheet{
          padding:17px!important;
          border-radius:22px!important;
          animation:moHostPopMobile .22s ease both;
        }

        @keyframes moHostPopMobile{
          from{opacity:0;transform:translateY(12px) scale(.985)}
          to{opacity:1;transform:translateY(0) scale(1)}
        }

        .moHostSheetAvatar{
          width:60px!important;
          height:60px!important;
          border-radius:18px!important;
        }

        .moHostSheetHead h2{
          font-size:20px!important;
        }

        .moHostSheetFoot{
          grid-template-columns:1fr!important;
        }

        .moOpenHost{
          width:100%!important;
        }
      }

      @media(max-width:470px){
        .moPlaceSheet{
          grid-template-columns:98px minmax(0,1fr)!important;
        }

        .moPlaceSheetVisual{
          min-height:176px!important;
        }

        .moHostCapabilities span{
          padding:6px 8px!important;
          font-size:7px!important;
        }
      }


      /* =========================================================
         EXPLORE V4 — SIMPLE MAP
         The map is the product. Controls stay quiet.
         ========================================================= */

      .moExplore{
        position:fixed!important;
        inset:0!important;
        z-index:99999!important;
        min-height:100svh!important;
        background:#e8eee6!important;
      }

      .moMapStage{
        height:100svh!important;
        min-height:0!important;
        background:#e8eee6!important;
      }

      .moMapShade,
      .moMapVignette{
        display:none!important;
      }

      .moLeaflet{
        filter:saturate(.86) contrast(.96) brightness(1.02);
      }

      /* Small, clear top bar. */
      .moMapHeader{
        top:14px!important;
        right:14px!important;
        left:14px!important;
        display:flex!important;
        align-items:center!important;
        justify-content:space-between!important;
        pointer-events:none;
      }

      .moBrand,
      .moHeaderActions{
        pointer-events:auto;
      }

      .moBrand{
        min-height:44px!important;
        padding:5px 11px 5px 6px!important;
        border:1px solid rgba(24,54,36,.08)!important;
        border-radius:16px!important;
        background:rgba(255,255,255,.94)!important;
        color:#173b27!important;
        box-shadow:0 10px 30px rgba(20,42,28,.10)!important;
        backdrop-filter:blur(18px)!important;
      }

      .moBrand>span{
        width:34px!important;
        height:34px!important;
        border-radius:11px!important;
        background:#173f2c!important;
        color:#cfffbb!important;
      }

      .moBrand strong{
        color:#173b27!important;
        font-size:8px!important;
      }

      .moBrand small{
        color:#748078!important;
        font-size:7px!important;
      }

      .moHeaderActions{
        gap:7px!important;
      }

      .moHeaderActions button,
      .moHeaderActions a{
        min-height:44px!important;
        border:1px solid rgba(24,54,36,.08)!important;
        border-radius:14px!important;
        background:rgba(255,255,255,.94)!important;
        color:#173b27!important;
        box-shadow:0 10px 30px rgba(20,42,28,.10)!important;
      }

      .moHeaderDiscover{
        background:#173f2c!important;
        color:#fff!important;
      }

      /* Compact search + filters only. */
      .moCommand{
        top:auto!important;
        right:auto!important;
        bottom:18px!important;
        left:18px!important;
        width:min(410px,calc(100% - 36px))!important;
        max-height:none!important;
        padding:12px!important;
        overflow:visible!important;
        border:1px solid rgba(24,54,36,.08)!important;
        border-radius:20px!important;
        background:rgba(255,255,255,.95)!important;
        color:#173b27!important;
        box-shadow:0 18px 48px rgba(20,42,28,.14)!important;
        backdrop-filter:blur(20px)!important;
      }

      .moCommandGlow,
      .moCommandCollapse,
      .moCommandBadge,
      .moNearMe,
      .moCommandFooter{
        display:none!important;
      }

      .moCommandIntro{
        display:flex!important;
        align-items:end!important;
        justify-content:space-between!important;
        gap:12px!important;
        margin:0 0 9px!important;
      }

      .moCommandIntro h1{
        margin:0!important;
        color:#173b27!important;
        font-size:19px!important;
        line-height:1!important;
        letter-spacing:-.04em!important;
      }

      .moCommandIntro h1 em{
        color:inherit!important;
        font-style:normal!important;
      }

      .moCommandIntro p{
        max-width:180px!important;
        margin:0!important;
        color:#728077!important;
        font-size:8px!important;
        line-height:1.35!important;
        text-align:right;
      }

      .moSearch{
        min-height:44px!important;
        margin:0!important;
        border:1px solid #e1e7e2!important;
        border-radius:13px!important;
        background:#f5f7f4!important;
        box-shadow:none!important;
      }

      .moSearch input{
        color:#173b27!important;
        font-size:11px!important;
      }

      .moSearch input::placeholder{
        color:#87938b!important;
      }

      .moSearchIcon,
      .moSearch button{
        color:#4a6252!important;
      }

      .moLayerFilters,
      .moQuickFilters{
        display:flex!important;
        gap:6px!important;
        margin-top:8px!important;
        padding-bottom:1px!important;
        overflow-x:auto!important;
        scrollbar-width:none!important;
      }

      .moLayerFilters::-webkit-scrollbar,
      .moQuickFilters::-webkit-scrollbar{
        display:none!important;
      }

      .moLayerFilters button,
      .moQuickFilters button{
        flex:0 0 auto!important;
        min-height:32px!important;
        padding:0 10px!important;
        border:1px solid #dfe5e0!important;
        border-radius:999px!important;
        background:#fff!important;
        color:#6e7c73!important;
        font-size:7px!important;
        font-weight:850!important;
        box-shadow:none!important;
      }

      .moLayerFilters button.active,
      .moQuickFilters button.active{
        border-color:#173f2c!important;
        background:#173f2c!important;
        color:#fff!important;
      }

      .moPassportHud,
      .moFloatingDiscover{
        display:none!important;
      }

      /* --- Markers --- */
      .moSimplePlaceMarkerShell,
      .moActivePlaceMarkerShell,
      .moSimpleHostMarkerShell{
        background:transparent!important;
        border:0!important;
      }

      .moSimplePlaceMarker{
        position:relative;
        width:34px;
        height:40px;
        cursor:pointer;
        filter:drop-shadow(0 8px 14px rgba(18,44,28,.22));
        transition:transform .16s ease;
      }

      .moSimplePlaceMarker:hover{
        transform:translateY(-2px) scale(1.04);
      }

      .moSimplePlaceMarker>span{
        position:absolute;
        left:4px;
        top:2px;
        width:26px;
        height:26px;
        border:3px solid #fff;
        border-radius:50% 50% 50% 8px;
        background:#173f2c;
        transform:rotate(-45deg);
        box-shadow:0 5px 15px rgba(0,0,0,.18);
      }

      .moSimplePlaceMarker>span::after{
        content:"";
        position:absolute;
        inset:8px;
        border-radius:50%;
        background:#cfffbb;
      }

      .moSimplePlaceMarker>i{
        display:none;
      }

      .moActivePlaceMarker{
        position:relative;
        width:132px;
        height:124px;
        cursor:pointer;
        filter:drop-shadow(0 16px 30px rgba(16,40,25,.28));
      }

      .moActivePlacePhoto{
        position:absolute;
        inset:0 0 14px;
        overflow:hidden;
        border:3px solid #fff;
        border-radius:19px;
        background:#173f2c;
        box-shadow:0 0 0 3px #173f2c;
      }

      .moActivePlacePhoto img{
        width:100%;
        height:100%;
        object-fit:cover;
        display:block;
      }

      .moActivePlaceShade{
        position:absolute;
        inset:0;
        background:linear-gradient(180deg,rgba(6,18,11,.02),rgba(6,18,11,.10) 40%,rgba(6,18,11,.82));
      }

      .moActivePlacePhoto span{
        position:absolute;
        top:8px;
        left:8px;
        max-width:108px;
        padding:4px 6px;
        overflow:hidden;
        border-radius:999px;
        background:rgba(9,25,15,.68);
        color:#dffff0;
        font-size:5px;
        font-weight:900;
        text-overflow:ellipsis;
        text-transform:uppercase;
        white-space:nowrap;
        backdrop-filter:blur(7px);
      }

      .moActivePlacePhoto strong{
        position:absolute;
        right:8px;
        bottom:8px;
        left:8px;
        color:#fff;
        font-size:8px;
        line-height:1.12;
        text-shadow:0 2px 8px rgba(0,0,0,.45);
      }

      .moActivePlaceMarker>i{
        position:absolute;
        left:54px;
        bottom:3px;
        width:19px;
        height:19px;
        border-right:3px solid #fff;
        border-bottom:3px solid #fff;
        background:#173f2c;
        transform:rotate(45deg);
        z-index:-1;
      }

      .moSimpleHostMarker{
        position:relative;
        min-width:48px;
        height:52px;
        display:flex;
        align-items:flex-start;
        gap:7px;
        cursor:pointer;
        filter:drop-shadow(0 8px 16px rgba(18,44,28,.24));
        transition:transform .16s ease;
      }

      .moSimpleHostMarker:hover{
        transform:translateY(-2px) scale(1.04);
      }

      .moSimpleHostAvatar{
        position:relative;
        z-index:2;
        width:46px;
        height:46px;
        flex:0 0 auto;
        overflow:hidden;
        display:grid;
        place-items:center;
        border:3px solid #fff;
        border-radius:15px;
        background:#173f2c;
        color:#fff;
        font-size:13px;
        font-weight:950;
        box-shadow:0 0 0 3px #173f2c,0 8px 20px rgba(0,0,0,.20);
      }

      .moSimpleHostAvatar img{
        position:absolute;
        inset:0;
        width:100%;
        height:100%;
        display:block;
        object-fit:cover;
        object-position:center;
        background:#173f2c;
      }

      .moSimpleHostMarker>strong{
        max-width:96px;
        margin-top:7px;
        padding:8px 10px;
        overflow:hidden;
        border:1px solid rgba(23,63,44,.10);
        border-radius:12px;
        background:rgba(255,255,255,.96);
        color:#173f2c;
        font-size:8px;
        font-weight:900;
        text-overflow:ellipsis;
        white-space:nowrap;
        box-shadow:0 7px 18px rgba(20,42,28,.12);
      }

      .moSimpleHostMarker>i{
        position:absolute;
        z-index:1;
        left:16px;
        top:35px;
        width:12px;
        height:12px;
        border-right:3px solid #fff;
        border-bottom:3px solid #fff;
        background:#173f2c;
        transform:rotate(45deg);
      }

      .moSimpleHostMarkerShell.active .moSimpleHostAvatar{
        box-shadow:0 0 0 4px #cfffbb,0 10px 22px rgba(0,0,0,.22);
      }

      /* Popup cards: white, minimal, easier to read on the map. */
      .moPlaceSheet,
      .moHostSheet{
        border:1px solid rgba(24,54,36,.10)!important;
        background:rgba(255,255,255,.97)!important;
        color:#173b27!important;
        box-shadow:0 24px 70px rgba(20,42,28,.18)!important;
        backdrop-filter:blur(20px)!important;
      }

      .moPopupAccent{
        background:linear-gradient(90deg,transparent,#173f2c,transparent)!important;
        opacity:.3!important;
      }

      .moPlaceSheetMeta>span,
      .moPlaceSheetMeta em,
      .moPlaceSheetDescription,
      .moHostSheetHead p,
      .moHostSheetFoot>div strong{
        color:#6f7c73!important;
      }

      .moPlaceSheet h2,
      .moHostSheetHead h2{
        color:#173b27!important;
      }

      .moHostSheetIdentity>span,
      .moHostSheetIdentity>small{
        color:#2f6243!important;
      }

      .moHostSheetClose,
      .moPlaceSheetClose{
        border-color:#e1e7e2!important;
        background:#f4f6f3!important;
        color:#38503f!important;
      }

      .moHostSheetAvatar{
        border-color:#fff!important;
        background:#cfffbb!important;
        color:#173f2c!important;
        box-shadow:0 8px 22px rgba(20,42,28,.12)!important;
      }

      .moHostSheetVerified{
        border-color:#fff!important;
      }

      .moHostSheetDivider{
        background:#e8ece8!important;
      }

      .moHostCapabilities span{
        border-color:#e1e7e2!important;
        background:#f4f6f3!important;
        color:#516158!important;
      }

      .moOpenHost,
      .moOpenPlace{
        background:#173f2c!important;
        color:#fff!important;
      }

      .moNavigatePlace{
        border-color:#e0e6e1!important;
        background:#f4f6f3!important;
        color:#173f2c!important;
      }

      .moPlaceSheetStats>span{
        border-color:#e2e7e3!important;
        background:#f6f8f5!important;
        color:#244d37!important;
      }

      .moProtectedNote{
        background:#f1f5f1!important;
        color:#5d6e63!important;
      }

      @media(max-width:700px){
        .moExplore{
          z-index:2147483000!important;
        }

        .moMapHeader{
          top:10px!important;
          right:10px!important;
          left:10px!important;
        }

        .moBrand{
          min-height:40px!important;
          padding-right:8px!important;
        }

        .moBrand>span{
          width:30px!important;
          height:30px!important;
        }

        .moBrand small,
        .moHeaderActions span{
          display:none!important;
        }

        .moHeaderActions button,
        .moHeaderActions a{
          width:40px!important;
          min-height:40px!important;
          padding:0!important;
        }

        .moCommand{
          right:10px!important;
          bottom:10px!important;
          left:10px!important;
          width:auto!important;
          padding:10px!important;
          border-radius:17px!important;
        }

        .moCommandIntro{
          display:none!important;
        }

        .moSearch{
          min-height:41px!important;
        }

        .moLayerFilters,
        .moQuickFilters{
          margin-top:7px!important;
        }

        .moLayerFilters button,
        .moQuickFilters button{
          min-height:30px!important;
          padding:0 9px!important;
          font-size:7px!important;
        }

        .moPlaceSheet{
          right:10px!important;
          bottom:106px!important;
          left:10px!important;
          width:auto!important;
          grid-template-columns:104px minmax(0,1fr)!important;
          border-radius:18px!important;
        }

        .moPlaceSheetVisual{
          min-height:174px!important;
        }

        .moPlaceSheetBody{
          padding:12px!important;
        }

        .moPlaceSheet h2{
          font-size:18px!important;
        }

        .moPlaceSheetDescription,
        .moPlaceSheetStats small,
        .moNavigatePlace{
          display:none!important;
        }

        .moPlaceSheetActions{
          grid-template-columns:1fr!important;
        }

        .moHostSheet{
          right:10px!important;
          bottom:106px!important;
          left:10px!important;
          width:auto!important;
          transform:none!important;
          padding:15px!important;
          border-radius:18px!important;
        }

        .moHostSheetAvatar{
          width:56px!important;
          height:56px!important;
          border-radius:17px!important;
        }

        .moHostSheetHead h2{
          font-size:19px!important;
        }

        .moHostSheetFoot{
          grid-template-columns:1fr!important;
          gap:10px!important;
        }

        .moOpenHost{
          width:100%!important;
        }

        .moActivePlaceMarker{
          width:116px;
          height:112px;
        }

        .moActivePlaceMarker>i{
          left:47px;
        }
      }


      /* =========================================================
         FILTER CARD — PREMIUM COMPACT PASS
         ========================================================= */

      .moCommand{
        padding:11px 11px 10px!important;
        border:1px solid rgba(18,53,34,.10)!important;
        border-radius:22px!important;
        background:
          linear-gradient(180deg,rgba(255,255,255,.985),rgba(248,250,247,.97))!important;
        box-shadow:
          0 22px 60px rgba(19,44,28,.16),
          0 2px 10px rgba(19,44,28,.05)!important;
        backdrop-filter:blur(26px) saturate(1.08)!important;
      }

      .moSearch{
        position:relative!important;
        min-height:46px!important;
        padding:0 8px!important;
        border:1px solid rgba(29,67,44,.09)!important;
        border-radius:15px!important;
        background:#fff!important;
        box-shadow:
          inset 0 0 0 1px rgba(255,255,255,.7),
          0 6px 18px rgba(23,59,39,.04)!important;
      }

      .moSearch::after{
        content:"";
        position:absolute;
        right:12px;
        bottom:-9px;
        left:12px;
        height:1px;
        background:linear-gradient(90deg,transparent,#dfe7e0,transparent);
        pointer-events:none;
      }

      .moSearchIcon{
        width:34px!important;
        height:34px!important;
        display:grid!important;
        place-items:center!important;
        border-radius:11px!important;
        background:#f1f5f1!important;
        color:#315842!important;
      }

      .moSearch input{
        font-size:11px!important;
        font-weight:700!important;
        letter-spacing:-.01em!important;
      }

      .moLayerFilters{
        margin-top:15px!important;
        padding:1px 1px 2px!important;
        gap:7px!important;
      }

      .moLayerFilters button{
        min-height:34px!important;
        padding:0 12px!important;
        border:1px solid #e2e8e3!important;
        border-radius:12px!important;
        background:#f9faf8!important;
        color:#5e6f64!important;
        font-size:7.5px!important;
        font-weight:900!important;
        letter-spacing:.01em!important;
        transition:
          transform .16s ease,
          background .16s ease,
          border-color .16s ease,
          color .16s ease!important;
      }

      .moLayerFilters button:hover{
        transform:translateY(-1px);
        border-color:#ccd9cf!important;
        color:#294a36!important;
      }

      .moLayerFilters button.active{
        border-color:#173f2c!important;
        background:
          linear-gradient(145deg,#1d4a33,#143522)!important;
        color:#fff!important;
        box-shadow:0 7px 16px rgba(23,63,44,.16)!important;
      }

      .moQuickFilters{
        margin-top:7px!important;
        padding-top:7px!important;
        border-top:1px solid #edf1ed!important;
      }

      .moQuickFilters button{
        min-height:29px!important;
        padding:0 9px!important;
        border-color:#e8ece9!important;
        background:#fff!important;
        color:#7b877f!important;
        font-size:6.5px!important;
      }

      .moQuickFilters button.active{
        border-color:#cfe0d2!important;
        background:#edf5ef!important;
        color:#234c35!important;
        box-shadow:none!important;
      }

      @media(max-width:700px){
        .moCommand{
          right:8px!important;
          bottom:8px!important;
          left:8px!important;
          padding:9px!important;
          border-radius:19px!important;
          box-shadow:
            0 16px 42px rgba(19,44,28,.16),
            0 1px 6px rgba(19,44,28,.05)!important;
        }

        .moSearch{
          min-height:43px!important;
          border-radius:13px!important;
        }

        .moSearchIcon{
          width:31px!important;
          height:31px!important;
          border-radius:10px!important;
        }

        .moLayerFilters{
          margin-top:13px!important;
          gap:6px!important;
        }

        .moLayerFilters button{
          min-height:32px!important;
          padding:0 10px!important;
          border-radius:11px!important;
          font-size:7px!important;
        }

        .moQuickFilters{
          gap:5px!important;
        }

        .moQuickFilters button{
          min-height:28px!important;
          font-size:6px!important;
        }

        .moSimpleHostAvatar{
          width:44px!important;
          height:44px!important;
          border-radius:14px!important;
        }
      }

    `}</style>
  );
}
